import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { FC, useState } from 'react'

import { fetchRequestFileBlob } from '@/api/models/requests/lib/requests.utils'
import { cn } from '@/app/utils'
import {
  DownloadedFileIcon,
  useRemoteFileSize
} from '@/components/shared/file-viewer'
import { downloadFileByLink, openUrlInNewTab } from '@/utils/file'

type Props = {
  label: string
  link?: string
}

const PDF_EXTENSION = '.pdf'

const UserDocumentCard: FC<Props> = ({ label, link }) => {
  const fileSizeWithUnit = useRemoteFileSize(link)
  const [isDownloading, setIsDownloading] = useState(false)

  const fileName = `${label}${PDF_EXTENSION}`

  const onDownloadClick = async () => {
    if (!link) {
      return
    }

    setIsDownloading(true)
    const file = await fetchRequestFileBlob({ name: fileName, link })
    setIsDownloading(false)

    if (file) {
      downloadFileByLink(fileName, file)
      return
    }

    openUrlInNewTab(link)
  }

  return (
    <div
      className={cn(
        'relative flex items-center gap-12 rounded-12 bg-white p-16',
        !link && 'opacity-50'
      )}
    >
      <div className="flex h-32 w-32 flex-shrink-0 items-center justify-center">
        <DownloadedFileIcon fileName={fileName} />
      </div>

      <div className="grow">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'font-pro transition-colors text-16 after:absolute after:inset-0',
              'hover:text-bronze-60 focus-visible:underline'
            )}
          >
            {label}
          </a>
        ) : (
          <span className="font-pro text-16">{label}</span>
        )}

        {fileSizeWithUnit && (
          <div className="mt-2 text-grey-70 text-12">{fileSizeWithUnit}</div>
        )}
      </div>

      <button
        type="button"
        aria-label={`Скачать документ: ${label}`}
        disabled={!link || isDownloading}
        className={cn(
          'relative z-10 flex h-20 w-20 flex-shrink-0 items-center justify-center',
          'text-grey-70 transition-colors',
          'enabled:cursor-pointer enabled:hover:text-grey-100',
          'disabled:opacity-50'
        )}
        onClick={onDownloadClick}
      >
        <ArrowDownTrayIcon className="h-20 w-20" />
      </button>
    </div>
  )
}

export default UserDocumentCard
